import * as moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridApi, GridColumnsChangedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, RowNode, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { ICenterBillingReport } from '@nkr/models/index';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import { Utility } from '@nkr/services/utility';
import AlleleMatch from '@nkr/components/allele-match';
import { flag } from 'react-icons-kit/icomoon/flag';
import { Label, OverlayTrigger } from 'react-bootstrap';
import Icon from 'react-icons-kit';
import PopoverTemplate from '@nkr/components/popover-template';
import { SerologyBillable } from './serology-billable';

interface ISortableHeaderWithFiltering extends React.PropsWithChildren<IHeaderParams> {
    enableColumnFiltering: boolean
}

const SortableHeaderComponentWithFiltering: React.FC<ISortableHeaderWithFiltering> = (props) => {
    return (
        <SortableHeaderComponent {...props} enableMenu={props.enableColumnFiltering} menuIconLocation='RIGHT' menuIcon='filter'>
            {props.children}
        </SortableHeaderComponent>
    );
}

interface IProps {
    data: ICenterBillingReport[],
    enableColumnFiltering: boolean,
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);

    useEffect(() => {
        let columns = getColumns();
        setColumnDefs(columns);
    }, [props.enableColumnFiltering]);

    useEffect(() => {
        let columns = getColumns();
        setColumnDefs(columns);
    }, []);

    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
        e.api.sizeColumnsToFit();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        // get the current grids width
        let gridWidth = document.getElementById('grid-wrapper').offsetWidth;

        // keep track of which columns to hide/show
        let columnsToShow = [];
        let columnsToHide = [];

        // iterate over all columns (visible or not) and work out
        // now many columns can fit (based on their minWidth)
        let totalColsWidth = 0;
        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];
            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        // show/hide columns based on current grid width
        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        // fill out any available space to ensure there are no gaps
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const createTopData = () => {
        let result = [];

        let transportCost = props.data.reduce((a, v) => a = a + v.transportCost, 0);
        let evalAmount = props.data.reduce((a, v) => a = a + v.evalAmount, 0);

        result.push({
            rank: 'Total:',
            transplantDate: '',
            invoiceDate: '',
            transportCost,
            evalAmount,
            recipientCpra4: '',
            micrositeActivationDate: ''
        });

        return result;
    };

    const getColumns = () => {
        const reportColumns: ColDef[] = [
            {
                colId: 'Rank',
                field: 'rank',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        R#
                    </SortableHeaderComponent>,
                pinnedRowCellRendererFramework: () => <span style={{ fontSize: "14px" }}>Totals:</span>,
                headerName: 'R',
                width: 40,
                sortable: false,
                suppressSizeToFit: true,
                valueGetter: (params: ValueGetterParams) => params.node.rowPinned ? '' : params.node.rowIndex + 1
            },
            {
                colId: 'txpDate',
                field: 'transplantDate',
                headerName: 'TXP Date',
                width: 100,
                filter: 'agDateColumnFilter',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        TXP<br />Date
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let transplantDate = params.data.transplantDate;

                    if (transplantDate != '') {
                        return (moment(transplantDate).format('MM/DD/YYYY'))
                    }

                    return '';
                },
                pinnedRowCellRendererFramework: () => <></>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;
                    return moment(data.transplantDate, 'MM/DD/YYYY').toDate();
                },
                comparator: Utility.dateComparator
            },
            {
                colId: 'micrositeWtMonths',
                field: 'micrositeWtMonths',
                headerName: 'micrositeWtMonths',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        MS<br />WT
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let micrositeWtMonths = params.data.micrositeWtMonths;

                    if (micrositeWtMonths != null) {
                        return <>{micrositeWtMonths}</>;
                    } else {
                        return <></>;
                    }
                },
                width: 60,
            },
            {
                colId: 'micrositeActivationDate',
                field: 'micrositeActivationDate',
                headerName: 'MS Act Date',
                width: 100,
                filter: 'agDateColumnFilter',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        MS Act<br />Date
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;

                    if (data.micrositeActivationDate) {
                        return (moment(data.micrositeActivationDate).format('MM/DD/YYYY'))
                    } else {
                        return <>-</>;
                    }
                },
                pinnedRowCellRendererFramework: () => <></>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;

                    if (data.micrositeActivationDate) {
                        return moment(data.micrositeActivationDate, 'MM/DD/YYYY').toDate();
                    } else {
                        return null;
                    }
                },
                //comparator: Utility.dateComparator
            },
            {
                colId: 'invoiceDate',
                field: 'invoiceDate',
                headerName: 'Invoice Date',
                width: 120,
                filter: 'agDateColumnFilter',
                filterParams: {
                    browserDataPicker: true
                },
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Inv<br />Date
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let invoiceDate = params.data.invoiceDate;

                    if (invoiceDate != '') {
                        return (moment(invoiceDate).format('MM/DD/YYYY'))
                    }

                    return '';
                },
                pinnedRowCellRendererFramework: () => <></>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;
                    return moment(data.invoiceDate, 'MM/DD/YYYY').toDate();
                },
                comparator: Utility.dateComparator
            },
            {
                colId: 'swapType',
                field: 'swapType',
                headerName: 'Swap Type',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Swap<br />Type
                    </SortableHeaderComponentWithFiltering>,
                width: 70,
            },
            {
                colId: 'chainId',
                field: 'chainId',
                headerName: 'Swap',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Swap
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;

                    if (data.chainId && data.cluster && data.position)
                        return (
                            <>{data.chainId}/{data.cluster}/{data.position}</>
                        );

                    return <></>
                },
                width: 85,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;
                    return data.chainId + '-' + data.cluster + '-' + data.position;
                }
            },
            {
                colId: 'remoteDonorCenterAlias',
                field: 'remoteDonorCenterAlias',
                headerName: 'Rem D Center',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        RD<br />Center
                    </SortableHeaderComponentWithFiltering>,
                width: 120,
            },
            {
                colId: 'donorCenter',
                field: 'donorCenter',
                headerName: 'Reg D Center',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        D<br />Center
                    </SortableHeaderComponentWithFiltering>,
                width: 120,
            },
            {
                colId: 'donorAlias',
                field: 'donorAlias',
                headerName: 'D Alias',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        D<br />Alias
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;
                    let { donorAlias, donorId, donorCenterId } = data;

                    return (<>
                        {donorId > 0 ? (
                            <a href={'center/' + donorCenterId + '/donor/' + donorId + '/profile'}>{donorAlias}</a>
                        ) : (
                            <span>{donorAlias}</span>
                        )}
                        {data.donorFlags && data.donorFlags.length > 0 && (
                            <>&nbsp;
                                <OverlayTrigger
                                    rootClose
                                    placement='top'
                                    trigger='hover'
                                    overlay={<PopoverTemplate>{data.donorFlags[0]}</PopoverTemplate>}
                                >
                                    <Icon icon={flag} size={12} style={{ color: 'red' }} />
                                </OverlayTrigger></>
                        )}
                    </>
                    )
                },
                width: 120,
            },
            {
                colId: 'donorRelationship',
                field: 'donorRelationship',
                headerName: 'D Relationship',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        D<br />Relationship
                    </SortableHeaderComponentWithFiltering>,
                width: 130,
            },
            {
                colId: 'recipientCenter',
                field: 'recipientCenter',
                headerName: 'R Center',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        R<br />Center
                    </SortableHeaderComponentWithFiltering>,
                width: 120,
            },
            {
                colId: 'recipientAlias',
                field: 'recipientAlias',
                headerName: 'R Alias',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        R<br />Alias
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;
                    let { recipientAlias, recipientId, recipientCenterId } = data;

                    let recipientAliasDisplay = recipientAlias;
                    if (recipientAlias != undefined && recipientAlias.length > 13) {
                        recipientAliasDisplay = recipientAlias.substring(0, 13) + '...';
                    }

                    return (recipientId > 0 ? <a target={'_blank'} href={"/center/" + recipientCenterId + "/recipient/" + recipientId + "/profile/general"}>{recipientAliasDisplay}</a> : <span>{recipientAliasDisplay}</span>)
                },
                width: 120,
            },
            {
                colId: 'emm',
                field: 'emm',
                headerName: 'EMM',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        EMM
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;
                    let { singleEpletMmRisk } = data;

                    return (
                        <SingleEpletMmRisk data={singleEpletMmRisk} isAdmin={false} centerId={data.recipientCenterId} placement='bottom' />
                    )
                },
                cellRendererSelector: (params: ICellRendererParams) => {
                    if (params.node.rowPinned) {
                        return {
                            frameworkComponent: () => <></>
                        };
                    }
                },
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;
                    let { singleEpletMmRisk } = data;

                    if (singleEpletMmRisk != null)
                        return singleEpletMmRisk.sortValue;
                    else
                        return 99999;
                },
                width: 80,
            },
            {
                colId: 'bmtMm',
                field: 'bmtMm',
                headerName: 'Allele Match',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Allele<br />Match
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;
                    return (
                        <AlleleMatch bmtMm={data.bmtMm} displayAsInline={false} isPinnedRow={true} />
                    );
                },
                comparator: (valueA: number, valueB: number) => {
                    if (valueA == null && valueB == null) return 0;
                    if (valueA == null) return -1;
                    if (valueB == null) return 1;
                    return Math.abs(valueA) - Math.abs(valueB);
                },
                width: 75,
            },
            {
                colId: 'shipMethod',
                field: 'shipMethod',
                headerName: 'Ship Method',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        SM
                    </SortableHeaderComponentWithFiltering>,
                width: 65,
            },
            {
                colId: 'transportVendor',
                field: 'transportVendor',
                headerName: 'Cour',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Cour
                    </SortableHeaderComponentWithFiltering>,
                width: 60,
            },
            {
                colId: 'transportCost',
                field: 'transportCost',
                headerName: 'Cour Cost',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Cour<br />Cost
                    </SortableHeaderComponentWithFiltering>,
                cellRendererFramework: (params: ICellRendererParams) => {
                    if (params.data.transportCost > 0) {
                        return params.data.transportCost.toLocaleString('en', { minimumFractionDigits: 0 });
                    } else {
                        return '';
                    }
                },
                valueGetter: (params: ValueGetterParams) => params.data.transportCost,
                width: 85,
                cellStyle: { 'justify-content': "flex-end" },
            },

            {
                colId: 'donorType',
                field: 'donorType',
                headerName: 'D Type',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        D<br />Type
                    </SortableHeaderComponentWithFiltering>,
                cellStyle: (params: any) => {
                    let data = params.data as ICenterBillingReport;
                    let donorType = data.donorType;

                    switch (donorType) {
                        case 'IKPD':
                            //mark police cells as red
                            return { backgroundColor: '#ffcccb' };
                        case 'NKR KPD':
                            return { backgroundColor: '#ffff66' }
                        case 'SV':
                        case 'NKR Swap':
                        case 'NKR KPD':
                            if (data.swapType == '--') {
                                return { backgroundColor: '#ffff66' }
                            }

                            break;
                    }

                    return null;
                },
                width: 80,
            },

            {
                colId: 'recipientType',
                field: 'recipientType',
                headerName: 'R Type',
                width: 80,
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        R<br />Type
                    </SortableHeaderComponentWithFiltering>,
                cellStyle: params => {
                    if (params.value === 'IKPD') {
                        //mark police cells as red
                        return { backgroundColor: '#ffcccb' };
                    }
                    return null;
                },
            },

            {
                colId: 'organType',
                field: 'organType',
                headerName: 'Organ Type',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
                width: 150,
                cellRendererFramework: (params: ICellRendererParams) => {
                    if (params.node.rowPinned) return <></>;   // ← skip pinned row
                    let data = params.data as ICenterBillingReport;
                    let organType = data.organType == null ? '--' : data.organType == 1 ? 'L' : 'K';
                    return <Label bsStyle={data.organType == null ? 'default' : data.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
                }
            },
            {
                colId: 'isCompatiblePairCpr',
                field: 'isCompatiblePairCpr',
                headerName: 'Compatible Pair',
                width: 70,
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Comp <br /> Pair
                    </SortableHeaderComponentWithFiltering>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;
                    return data.isCompatiblePairCpr ? "Y" : (data.recipientWasIncompatiblePair ? "N" : "");
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;
                    return data.isCompatiblePairCpr ? "Y" : (data.recipientWasIncompatiblePair ? <span className={'label label-danger'}>N</span> : "");
                },
                pinnedRowCellRendererFramework: () => <></>,
            },
            {
                colId: 'isMicrositeFacilitated',
                field: 'isMicrositeFacilitated',
                headerName: 'Microsite',
                width: 65,
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        MS
                    </SortableHeaderComponentWithFiltering>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;
                    return data.isMicrositeFacilitated ? (data.hasMsFeeWaiver ? "W" : "Y") : "";
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;
                    return data.isMicrositeFacilitated ? <a target={'_blank'} href={"/admin/microsite/" + data.micrositeRecipientId + "/registration"}>{data.hasMsFeeWaiver ? "W" : "Y"}</a> : "";
                },
                pinnedRowCellRendererFramework: () => <></>,
            },
            {
                colId: 'recipientHasMedicareAb',
                field: 'recipientHasMedicareAb',
                headerName: 'Has Med',
                width: 65,
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Has<br />Med
                    </SortableHeaderComponentWithFiltering>,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as ICenterBillingReport;
                    return data.recipientHasMedicareAb ? "Y" : "";
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as ICenterBillingReport;
                    return data.recipientHasMedicareAb ? "Y" : "";
                },
                pinnedRowCellRendererFramework: () => <></>,
            },
            {
                colId: 'serologyIsBillable',
                field: 'serologyIsBillable',
                headerName: 'Sero Bill',
                width: 65,
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        Sero<br />Bill
                    </SortableHeaderComponentWithFiltering>,
                valueGetter: (params: ValueGetterParams) => {
                    if (params.node.rowPinned) return '';
                    let data = params.data as ICenterBillingReport;
                    return data.serologyIsBillable ? "Y" : "N";
                },
                cellRendererFramework: (params: ICellRendererParams) => {
                    if (params.node.rowPinned) return <></>;
                    return <SerologyBillable data={params.data as ICenterBillingReport} />;
                },
            },
            {
                colId: 'intendedDonorAbo',
                field: 'intendedDonorAbo',
                headerName: 'PD ABO',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        PD<br />ABO
                    </SortableHeaderComponentWithFiltering>,
                width: 60,
            },

            {
                colId: 'recipientBloodType',
                field: 'recipientBloodType',
                headerName: 'R ABO',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        R<br />ABO
                    </SortableHeaderComponentWithFiltering>,
                width: 60,
            },

            {
                colId: 'recipientCpra4',
                field: 'recipientCpra4',
                headerName: 'R cPRA',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        R<br />cPRA
                    </SortableHeaderComponentWithFiltering>,
                cellRendererSelector: (params: ICellRendererParams) => {
                    if (params.node.rowPinned) {
                        return {
                            frameworkComponent: () => <></>
                        };
                    }
                },
                valueGetter: (params: ValueGetterParams) => params.data.recipientCpra4 != '' ? parseFloat(params.data.recipientCpra4).toFixed(2) : '0.00',
                comparator: Utility.decimalComparator,
                width: 80,
            },
            {
                colId: 'waitTimeInDays',
                field: 'waitTimeInDays',
                headerName: 'WT',
                headerClass: 'center-list-header',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponentWithFiltering enableColumnFiltering={props.enableColumnFiltering} {...params}>
                        WT
                    </SortableHeaderComponentWithFiltering>,
                width: 65,
            }
        ];

        return reportColumns;
    }

    const rowData = useMemo(() => props.data, [props.data]);

    const totals = createTopData();

    return (
        <GridWrapper id='grid-wrapper' className='ag-theme-bootstrap' style={{ marginBottom: 20 }}>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'center-list-cell',
                    headerClass: 'center-list-header',
                    sortable: true,
                    resizable: true,
                    filter: props.enableColumnFiltering,
                    filterParams: {
                        inRangeInclusive: true,
                        // buttons: ['apply', 'clear', 'reset', 'cancel']
                    },
                    menuTabs: ['filterMenuTab']
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                enableCellTextSelection={true}
                enableRangeSelection={true}
                headerHeight={38}
                pinnedTopRowData={totals}
                getRowStyle={function (params) {
                    if (params.node.rowPinned) {
                        return { 'font-weight': 'bold', 'justify-content': 'right', 'background-color': 'lightgray' };
                    }
                }}
                noRowsOverlayComponentFramework={() => <div>No data for the selected date range...</div>}
                suppressRowHoverHighlight={false}
                suppressCellSelection={true}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onSortChanged={onSortChanged}
                onRowDataChanged={onRowDataChanged}
                onFirstDataRendered={(e: FirstDataRenderedEvent) => e.api.sizeColumnsToFit()}
                onGridColumnsChanged={(e: GridColumnsChangedEvent) => e.api.sizeColumnsToFit()}
            />
        </GridWrapper>
    );
}

export default Grid;